import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {requestFieldInsetsMeasure} from '../../../../utils/visuals/fieldInsets';

const LIFT_DURATION_MS = 340;
const LIFT_EASING = 'cubic-bezier(0.25, 0.8, 0.3, 1)';

// Анимации подъема: футер целиком едет снизу вверх, а ряд жетонов под карточкой едет ему
// навстречу и остается на месте. Без футера, свернутым футером и без браузерного animate
// (тесты) подъема нет — карточка открывается сразу
const createLift = (card) => {
  const footer = card?.closest('.app-footer');
  const isPossible = footer
    && !footer.classList.contains('collapsed')
    && typeof footer.animate === 'function'
    && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!isPossible) {
    return null;
  }
  const growth = card.offsetHeight + parseFloat(window.getComputedStyle(card).marginTop || 0);
  if (!(growth > 0)) {
    return null;
  }
  const timing = {duration: LIFT_DURATION_MS, easing: LIFT_EASING, fill: 'both'};
  const animations = [
    footer.animate([{transform: `translateY(${growth}px)`}, {transform: 'none'}], timing),
  ];
  if (card.nextElementSibling) {
    animations.push(card.nextElementSibling.animate([{transform: `translateY(${-growth}px)`}, {transform: 'none'}], timing));
  }
  // В начале подъема содержимое лежит поверх ряда жетонов: проявляется, когда отъехало
  const content = card.firstElementChild;
  if (content) {
    animations.push(content.animate([{opacity: 0, offset: 0}, {opacity: 0, offset: 0.3}, {opacity: 1, offset: 1}], timing));
  }
  animations.forEach((animation) => animation.pause());
  return animations;
};

/**
 * Раскрытие карточки над рядом жетонов вне десктопа. Анимировать высоту нельзя: футер
 * растет, и каждый кадр заново рисуется вся плита с текстурой и тенью — на телефоне это
 * заметная просадка FPS. Поэтому высота меняется сразу, один раз, а футер плавно едет
 * сдвигом уже нарисованного слоя. При закрытии наоборот: сначала сдвиг, в конце высота, а
 * плита на это время уходит за нижний край окна. Прерванное раскрытие разворачивается с
 * того же места
 *
 * @param {React.RefObject<HTMLElement>} cardRef - карточка
 * @param {boolean} isOpen - есть что показать
 * @param {boolean} isEnabled - карточка над рядом (isStacked)
 * @returns {boolean} карточка еще закрывается и должна оставаться развернутой
 */
export const useCardLift = (cardRef, isOpen, isEnabled) => {
  const [isClosing, setIsClosing] = useState(false);
  const liftRef = useRef(null);
  const wasOpenRef = useRef(isOpen);

  const stopLift = () => {
    liftRef.current?.forEach((animation) => animation.cancel());
    liftRef.current = null;
  };

  const runLift = (playbackRate, onFinish) => {
    liftRef.current = liftRef.current || createLift(cardRef.current);
    const lift = liftRef.current;
    if (!lift) {
      onFinish();
      return;
    }
    lift.forEach((animation) => {
      // Смена скорости сохраняет текущее положение: разворот идет с того же места
      animation.playbackRate = playbackRate;
      animation.play();
    });
    lift[0].onfinish = onFinish;
  };

  // Высота уже новая, а камера и отступы поля не видят ее за сдвигом футера
  const handleOpened = () => {
    stopLift();
    requestFieldInsetsMeasure();
  };

  // Снять развернутое состояние и сдвиг в одной задаче: иначе между ними мелькнул бы кадр
  // с плитой не на своем месте
  const handleClosed = () => {
    flushSync(() => setIsClosing(false));
    stopLift();
    requestFieldInsetsMeasure();
  };

  useLayoutEffect(() => {
    if (wasOpenRef.current === isOpen) {
      return;
    }
    wasOpenRef.current = isOpen;
    if (!isEnabled) {
      return;
    }
    if (isOpen) {
      setIsClosing(false);
      runLift(1, handleOpened);
    } else {
      setIsClosing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEnabled]);

  // Отдельным эффектом: чтобы измерить карточку, она должна снова стоять развернутой
  useLayoutEffect(() => {
    if (isClosing) {
      runLift(-1, handleClosed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosing]);

  useEffect(() => stopLift, []);

  return isClosing;
};
